import React from 'react';
import { Communication } from '../../services/communicationService';
import { formatDateTime } from '../../utils/dateUtils';

interface Props {
  item: Communication;
  onClick: (item: Communication) => void;
  userTimezone: string;
}

const CommunicationTimelineItem: React.FC<Props> = ({ item, onClick, userTimezone }) => {
  const outgoing = item.direction === 'OUTGOING';
  const when = formatDateTime(item.received_at || item.created_at, {}, userTimezone);
  const preview = (item.body_text || '').replace(/\s+/g, ' ').trim();
  const attachmentCount = item.attachments?.length || 0;

  return (
    <div className={`flex ${outgoing ? 'justify-end' : 'justify-start'}`}>
      <button
        type="button"
        onClick={() => onClick(item)}
        className={`text-left w-full max-w-[90%] rounded-lg px-3 py-2 border-l-4 transition-colors break-words ${
          outgoing
            ? 'bg-[#EBF5F5] border-[#3A7380] hover:bg-[#D0E7E5]'
            : 'bg-white border-gray-400 hover:bg-gray-50'
        } border-y border-r border-gray-200`}
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-600">
            <span className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-700">{item.channel}</span>
            <span>{outgoing ? '↗ Sent' : '↙ Received'}</span>
            {attachmentCount > 0 && (
              /* The row is one big button, so this only has to say that
                 something is attached; opening the message downloads it. */
              <span
                className="inline-flex items-center gap-0.5 text-gray-600"
                title={`${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}`}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                  />
                </svg>
                {attachmentCount}
              </span>
            )}
          </span>
          <span className="text-[11px] text-gray-400 whitespace-nowrap">{when}</span>
        </div>
        {item.channel === 'EMAIL' && (
          <div className="text-sm font-medium text-gray-900 truncate">
            {item.subject || '(no subject)'}
          </div>
        )}
        {preview && (
          <div className="text-xs text-gray-500 truncate">{preview}</div>
        )}
        {(item.status === 'FAILED' || item.status === 'UNMATCHED') && (
          <span className="inline-block mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700">
            {item.status}
          </span>
        )}
      </button>
    </div>
  );
};

export default CommunicationTimelineItem;
